import { FC, FormEvent, useEffect, useState } from "react";
import {
  createResource,
  deleteResource,
  listResource,
  updateResource,
} from "data/adminApi";
import ConfirmDialog from "components/Admin/ConfirmDialog";
import FileUploader from "components/Admin/FileUploader";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";

interface Client {
  id: number;
  name: string;
  location: string;
  company_url?: string;
  image?: string;
}

const empty = { name: "", location: "", company_url: "", image: "" };

const AdminClients: FC = () => {
  const [items, setItems] = useState<Client[]>([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => setItems(await listResource<Client>("/clients/"));

  useEffect(() => {
    load().catch(() => setMessage("Failed to load clients."));
  }, []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) await updateResource(`/clients/${editId}/`, form);
      else await createResource("/clients/", form);
      setForm(empty);
      setEditId(null);
      await load();
      setMessage("Client saved.");
    } catch {
      setMessage("Could not save client.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Clients</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Manage client logos shown on the website.
        </p>
      </div>
      {message && (
        <div className="rounded-xl bg-primary-50 text-primary-800 text-sm px-4 py-3">
          {message}
        </div>
      )}

      <form
        onSubmit={save}
        className="rounded-2xl border bg-white dark:bg-neutral-900 p-5 space-y-3"
      >
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            required
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="h-11 px-4 rounded-xl border"
          />
          <input
            required
            placeholder="Location"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            className="h-11 px-4 rounded-xl border"
          />
          <input
            placeholder="Company URL"
            value={form.company_url}
            onChange={(e) => setForm({ ...form, company_url: e.target.value })}
            className="h-11 px-4 rounded-xl border"
          />
          <input
            placeholder="Logo image URL"
            value={form.image}
            onChange={(e) => setForm({ ...form, image: e.target.value })}
            className="h-11 px-4 rounded-xl border"
          />
        </div>
        <FileUploader
          folder="clients"
          label="Upload logo"
          onUploaded={(url) => setForm({ ...form, image: url })}
        />
        {form.image && (
          <img
            src={form.image}
            alt=""
            className="h-16 object-contain rounded border bg-white p-2"
          />
        )}
        <button
          type="submit"
          disabled={loading}
          className="h-10 px-4 rounded-xl bcr-btn-primary text-sm font-semibold"
        >
          {editId ? "Update" : "Create"}
        </button>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <article
            key={c.id}
            className="rounded-2xl border bg-white dark:bg-neutral-900 p-4"
          >
            {c.image && (
              <img
                src={c.image}
                alt={c.name}
                className="h-16 object-contain mx-auto"
              />
            )}
            <h3 className="mt-3 font-semibold text-center">{c.name}</h3>
            <p className="text-xs text-neutral-500 text-center">{c.location}</p>
            <div className="mt-3 flex justify-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditId(c.id);
                  setForm({
                    name: c.name,
                    location: c.location,
                    company_url: c.company_url || "",
                    image: c.image || "",
                  });
                }}
                className="p-2 rounded-lg hover:bg-neutral-100"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setDeleteId(c.id)}
                className="p-2 rounded-lg hover:bg-red-50 text-red-600"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </article>
        ))}
      </div>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete client?"
        message="This client will be removed from the website."
        loading={loading}
        onClose={() => setDeleteId(null)}
        onConfirm={async () => {
          if (!deleteId) return;
          setLoading(true);
          try {
            await deleteResource(`/clients/${deleteId}/`);
            await load();
          } finally {
            setLoading(false);
            setDeleteId(null);
          }
        }}
      />
    </div>
  );
};

export default AdminClients;
